import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { useRightSidebar } from "./RightSidebarContext";
import { SIDEBAR_SECTIONS, NEW_UI } from "../Navigation/constants";

const LayoutContext = createContext(null);

const LEFT_WIDTH = 320;

// GS only (SIDEBAR_LENS_REWORK r4): the right aside is a FLOATING panel
// inset 16px from the viewport's right edge (Main.jsx `right-4`), so the
// center content must clear the panel's width PLUS that gap — otherwise the
// panel's left border rides over the content's last 16px and the right edge
// reads tighter than the left. DD's flush panel keeps the exact-width push.
const isGS = NEW_UI; // new UI on every variant (see constants.js)
const RIGHT_PANEL_GAP = isGS ? 16 : 0;

// Below this width, having both sidebars open squishes the center content.
// When one opens in this range, we auto-close the other.
const DUAL_SIDEBAR_MIN_WIDTH = 1700;

export const LayoutProvider = ({ children }) => {
  const location = useLocation();
  const currentAppPage =
    "/" + (location.pathname.split("/").filter(Boolean)[0] || "home");

  const sections = SIDEBAR_SECTIONS;

  const {
    isRightOpen,
    setIsRightOpen,
    rightComponent,
    setRightComponent,
    rightSidebarMode,
  } = useRightSidebar();

  const [rightWidth, setRightWidth] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem("rightSidebarWidth"), 10);
      return saved >= 360 && saved <= 900 ? saved : 512;
    } catch {
      return 512;
    }
  });
  const isResizingRight = useRef(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(512);

  const [isLeftOpen, setIsLeftOpen] = useState(true);
  const [baseMargin, setBaseMargin] = useState(64);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [hoverLeft, setHoverLeft] = useState(false);
  const [hoverRight, setHoverRight] = useState(false);
  const [forcedLeft, setForcedLeft] = useState(false);
  const [forcedRight, setForcedRight] = useState(false);

  const [currentCase, setCurrentCase] = useState(null);

  // Close right sidebar on page change
  useEffect(() => {
    try {
      setIsRightOpen(false);
      setRightComponent(null);
    } catch (e) {
      console.log(e);
    }
  }, [currentAppPage]);

  // Auto-close left nav when right sidebar opens on medium screens
  // to prevent the center content from getting squished.
  useEffect(() => {
    if (
      isRightOpen &&
      screenWidth >= 1250 &&
      screenWidth < DUAL_SIDEBAR_MIN_WIDTH
    ) {
      setIsLeftOpen(false);
    }
  }, [isRightOpen, screenWidth]);

  // Responsive margin + sidebar collapse logic
  useEffect(() => {
    const calcMargin = (w) => {
      if (currentAppPage === "/home") return 0;
      if (currentAppPage === "/chat") return 0;
      if (currentAppPage === "/explorer") return 0;
      if (currentAppPage === "/wiki") return 0;
      if (currentAppPage === "/graph") return 0;
      if (currentAppPage === "/feed") return 0;
      if (currentAppPage === "/reports") return 0;
      if (currentAppPage === "/threads") return 0;
      if (w > 2000) return 288;
      if (w > 1900) return 256;
      if (w > 1800) return 192;
      if (w > 1700) return 128;
      if (w > 1600) return 96;
      if (w > 1500) return 32;
      if (w > 1400) return 16;
      if (w > 1350) return 8;
      if (w > 1300) return 4;
      if (w > 1250) return 0;
      return 0;
    };

    const handleResize = () => {
      const w = window.innerWidth;
      setScreenWidth(w);
      setBaseMargin(calcMargin(w));
      if (w < 1250) {
        setIsLeftOpen(false);
        setIsRightOpen(false);
        setForcedLeft(false);
        setForcedRight(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [currentAppPage]);

  const leftOpen = screenWidth >= 1250 ? isLeftOpen : forcedLeft;
  const rightOpen = isRightOpen || (screenWidth >= 1250 ? false : forcedRight);

  // On medium screens, opening the left nav also closes the right sidebar
  // to prevent the center content from getting squished.
  const toggleLeft = useCallback(() => {
    if (screenWidth >= 1250) {
      const willOpen = !isLeftOpen;
      if (willOpen && screenWidth < DUAL_SIDEBAR_MIN_WIDTH && isRightOpen) {
        setIsRightOpen(false);
      }
      setIsLeftOpen(willOpen);
    } else {
      setForcedLeft((o) => !o);
    }
  }, [screenWidth, isLeftOpen, isRightOpen, setIsRightOpen]);

  // FIX: on small screens, simply toggle forcedRight — no auto-close timeout
  const toggleRight = useCallback(() => {
    if (screenWidth >= 1250) {
      setIsRightOpen((o) => !o);
    } else {
      setForcedRight((o) => !o);
    }
  }, [screenWidth, setIsRightOpen]);

  const headerMarginLeft = screenWidth >= 1250 && leftOpen ? LEFT_WIDTH : 0;

  const centerMarginLeft =
    screenWidth >= 1250
      ? leftOpen
        ? Math.max(LEFT_WIDTH, baseMargin)
        : baseMargin
      : baseMargin;

  // Recalls get a slightly wider sidebar (still respects a larger user-resized
  // width). Computed here so the center content flexes for it like any width.
  const effectiveRightWidth =
    rightSidebarMode === "recall" ? Math.max(rightWidth, 540) : rightWidth;

  const centerMarginRight =
    screenWidth >= 1250
      ? rightOpen
        ? Math.max(effectiveRightWidth + RIGHT_PANEL_GAP, baseMargin)
        : baseMargin
      : baseMargin;

  return (
    <LayoutContext.Provider
      value={{
        // Navigation
        currentAppPage,
        sections,
        currentCase,
        setCurrentCase,

        // Left sidebar
        isLeftOpen,
        setIsLeftOpen,
        leftOpen,
        toggleLeft,
        LEFT_WIDTH,
        hoverLeft,
        setHoverLeft,
        forcedLeft,
        setForcedLeft,

        // Right sidebar
        isRightOpen,
        setIsRightOpen,
        rightOpen,
        toggleRight,
        rightWidth,
        effectiveRightWidth,
        setRightWidth,
        rightComponent,
        setRightComponent,
        hoverRight,
        setHoverRight,
        forcedRight,
        setForcedRight,
        isResizingRight,
        resizeStartX,
        resizeStartWidth,

        // Layout metrics
        screenWidth,
        baseMargin,
        headerMarginLeft,
        centerMarginLeft,
        centerMarginRight,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error("useLayout must be used within LayoutProvider");
  return ctx;
};

export default LayoutContext;
